import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  Injector,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { UiSearchComponent } from '../ui-search/ui-search.component';
import { UiBottomSheetComponent } from '../ui-bottom-sheet/ui-bottom-sheet.component';
import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiSheetEmptyStateComponent } from '../ui-sheet-empty-state/ui-sheet-empty-state.component';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { UiTouchFeedbackDirective } from '../../directives/touch-feedback.directive';
import { TruncateDirective } from '../../directives/truncate.directive';
import {
  MobileChip,
  MobileFilterDef,
  MobileFilterOption,
  MobileFilterState,
} from './ui-mobile-header.model';
import { MobileTitleEditOptions } from './ui-mobile-header-title-edit.model';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';

const SHEET_SEARCH_MIN_OPTIONS = 8;
const TITLE_LONG_PRESS_MS = 500;
const TITLE_LONG_PRESS_SLOP_PX = 10;
const TITLE_RENAMED_HIGHLIGHT_MS = 1200;
const RENAME_COUNTER_WARNING_OFFSET = 5;
const RENAME_TITLE_KEY = 'MOBILE_HEADER.RENAME';
const ADD_NAME_TITLE_KEY = 'MOBILE_HEADER.ADD_NAME';
const DEFAULT_TITLE_DROPDOWN_LABEL = 'MOBILE_HEADER.BROWSE_SECTIONS';

@Component({
  selector: 'ui-mobile-header',
  templateUrl: './ui-mobile-header.component.html',
  styleUrls: ['./ui-mobile-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    MatIcon,
    ReactiveFormsModule,
    UiSearchComponent,
    UiBottomSheetComponent,
    UiButtonComponent,
    UiSheetEmptyStateComponent,
    UiTouchFeedbackDirective,
    TruncateDirective,
    MatRadioGroup,
    MatRadioButton,
  ],
})
export class UiMobileHeaderComponent {
  public readonly title = input.required<string>();
  public readonly subtitle = input<string>('');
  public readonly searchPlaceholder = input<string>('');
  public readonly searchQuery = input<string>('');
  public readonly filterDefs = input<MobileFilterDef[]>([]);
  public readonly activeFilters = input<MobileFilterState>({});
  public readonly chips = input<MobileChip[]>([]);
  public readonly showTitleDropdown = input<boolean>(false);
  public readonly titleDropdownLabel = input<string | null>(null);
  public readonly showFilterTrigger = input<boolean>(true);
  public readonly showBackButton = input<boolean>(false);
  public readonly titleEditable = input<boolean>(false);
  public readonly titlePlaceholder = input<string>('');
  public readonly titleEditOptions = input<MobileTitleEditOptions | null>(null);
  public readonly initialFilterMode = input<boolean>(false);
  public readonly selectionActive = input<boolean>(false);
  public readonly selectionLabel = input<string>('');
  public readonly showSelectAll = input<boolean>(false);
  public readonly allSelected = input<boolean>(false);

  public readonly backClick = output<void>();
  public readonly searchChange = output<string>();
  public readonly searchSubmit = output<string>();
  public readonly filterApply = output<{ key: string; values: unknown[] }>();
  public readonly customPillTap = output<string>();
  public readonly chipRemove = output<MobileChip>();
  public readonly clearAll = output<void>();
  public readonly titleDropdownClick = output<void>();
  public readonly titleChange = output<string>();
  public readonly selectionCancel = output<void>();
  public readonly selectionSelectAll = output<void>();

  private readonly translate = inject(TranslateService);
  private readonly langChange = toSignal(this.translate.onLangChange, {
    initialValue: null,
  });

  protected readonly displayTitle = computed<string>(() => {
    this.langChange();
    const raw = this.title();
    if (!raw) {
      return '';
    }
    const translated: unknown = this.translate.instant(raw);
    return typeof translated === 'string' ? translated : raw;
  });

  protected readonly isTitleInteractive = computed<boolean>(
    () => this.showTitleDropdown() || this.titleEditable(),
  );

  protected readonly titleAffixIcon = computed<string | null>(() => {
    if (this.showTitleDropdown()) {
      return 'ui-icon-expand-arrow';
    }
    return this.titleEditable() ? 'ui-icon-edit' : null;
  });

  protected readonly resolvedTitleDropdownLabel = computed<string>(
    () => this.titleDropdownLabel() ?? DEFAULT_TITLE_DROPDOWN_LABEL,
  );

  protected readonly titleSheetOpen = signal(false);
  protected readonly renameOpen = signal(false);
  protected readonly renameValue = signal('');
  protected readonly titleRenamed = signal(false);
  private readonly renameInputRef =
    viewChild<ElementRef<HTMLInputElement>>('renameInput');
  private titleLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private titleRenamedTimer: ReturnType<typeof setTimeout> | null = null;
  private titlePressOrigin: { x: number; y: number } | null = null;
  private suppressTitleClick = false;

  protected readonly renameSheetTitle = computed<string>(() =>
    this.title() ? RENAME_TITLE_KEY : ADD_NAME_TITLE_KEY,
  );

  protected readonly renamePlaceholder = computed<string>(
    () => this.titleEditOptions()?.placeholder ?? this.titlePlaceholder(),
  );

  protected readonly renameMaxLength = computed<number | null>(
    () => this.titleEditOptions()?.maxLength ?? null,
  );

  protected readonly renameCounterWarning = computed<boolean>(() => {
    const max = this.renameMaxLength();
    return (
      max !== null &&
      this.renameValue().length >= max - RENAME_COUNTER_WARNING_OFFSET
    );
  });

  protected readonly renameErrorKey = computed<string | null>(() => {
    const validate = this.titleEditOptions()?.validate;
    const value = this.renameValue().trim();
    if (!validate || !value || value === this.title()) {
      return null;
    }
    return validate(value);
  });

  protected readonly canSaveRename = computed<boolean>(() => {
    const value = this.renameValue().trim();
    return !!value && value !== this.title() && !this.renameErrorKey();
  });
  private readonly searchRef = viewChild<UiSearchComponent>('searchRef');

  protected readonly searchControl = new FormControl<string>('', {
    nonNullable: true,
  });

  private readonly searchValue = signal<string>(this.searchControl.value);

  protected readonly hasActiveFilters = computed<boolean>(
    () =>
      this.chips().length > 0 ||
      !!this.searchValue().trim() ||
      !!this.searchQuery().trim(),
  );

  protected readonly sheetSearchControl = new FormControl<string>('', {
    nonNullable: true,
  });

  protected readonly sheetSearchValue = toSignal<string>(
    this.sheetSearchControl.valueChanges.pipe(
      startWith(this.sheetSearchControl.value),
    ),
    { requireSync: true },
  );

  protected readonly filterMode = signal(false);
  protected readonly activeSheetKey = signal<string | null>(null);
  protected readonly localSheetValues = signal<unknown[]>([]);

  constructor() {
    effect(() => {
      const q = this.searchQuery();
      if (this.searchControl.value !== q) {
        this.searchControl.setValue(q, { emitEvent: false });
        this.searchValue.set(q);
      }
    });

    effect(() => {
      this.title();
      if (this.initialFilterMode()) {
        this.openFilterMode();
      } else {
        this.closeFilterMode();
      }
    });

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((v) => {
        this.searchValue.set(v);
        this.searchChange.emit(v);
      });

    effect(() => {
      if (!this.titleEditable()) {
        untracked(() => {
          this.titleSheetOpen.set(false);
          this.renameOpen.set(false);
        });
      }
    });

    inject(DestroyRef).onDestroy(() => {
      this.clearTitleLongPress();
      if (this.titleRenamedTimer) {
        clearTimeout(this.titleRenamedTimer);
      }
    });
  }

  protected readonly activeSheetDef = computed<MobileFilterDef | undefined>(
    () => {
      const key = this.activeSheetKey();
      if (!key) return undefined;
      return this.filterDefs().find((d) => d.key === key);
    },
  );

  protected readonly showSheetSearch = computed<boolean>(
    () =>
      (this.activeSheetDef()?.options?.length ?? 0) >= SHEET_SEARCH_MIN_OPTIONS,
  );

  protected readonly filteredSheetOptions = computed<MobileFilterOption[]>(
    () => {
      this.langChange();
      const options = this.activeSheetDef()?.options ?? [];
      const query = this.sheetSearchValue().trim().toLowerCase();
      if (!query) {
        return options;
      }
      return options.filter((option) =>
        this.optionLabel(option).toLowerCase().includes(query),
      );
    },
  );

  private readonly injector = inject(Injector);

  public openFilterMode(): void {
    this.filterMode.set(true);
    afterNextRender(() => this.searchRef()?.focus(), {
      injector: this.injector,
    });
  }

  public closeFilterMode(): void {
    this.filterMode.set(false);
  }

  public onPillTap(def: MobileFilterDef): void {
    if (def.locked) {
      return;
    }
    const type = def.type ?? 'options';
    if (type === 'options') {
      this.openSheet(def);
    } else {
      this.customPillTap.emit(def.key);
    }
  }

  public openSheet(def: MobileFilterDef): void {
    (document.activeElement as HTMLElement)?.blur();
    const current: unknown[] = this.activeFilters()[def.key] ?? [];
    this.localSheetValues.set([...current]);
    this.sheetSearchControl.setValue('');
    this.activeSheetKey.set(def.key);
  }

  public closeSheet(): void {
    this.activeSheetKey.set(null);
    this.sheetSearchControl.setValue('');
  }

  private optionLabel(option: MobileFilterOption): string {
    const translated: unknown = this.translate.instant(option.label);
    return typeof translated === 'string' ? translated : option.label;
  }

  public onRadioChange(value: unknown): void {
    this.localSheetValues.set([value]);
  }

  public toggleLocalValue(value: unknown): void {
    const current = this.localSheetValues();

    if (this.activeSheetDef()?.singleSelect) {
      this.localSheetValues.set(current.includes(value) ? [] : [value]);
      return;
    }

    const idx = current.indexOf(value);
    if (idx >= 0) {
      this.localSheetValues.set(current.filter((v) => v !== value));
    } else {
      this.localSheetValues.set([...current, value]);
    }
  }

  public applySheet(): void {
    const key = this.activeSheetKey();
    if (key) {
      this.filterApply.emit({
        key,
        values: this.localSheetValues(),
      });
    }
    this.closeSheet();
  }

  public onChipTap(chip: MobileChip): void {
    const def = this.filterDefs().find((d) => d.key === chip.filterKey);
    if (def) {
      this.onPillTap(def);
    }
  }

  public onChipRemove(chip: MobileChip): void {
    this.chipRemove.emit(chip);
  }

  public onSearchBarEnter(value: string): void {
    this.searchSubmit.emit(value);
  }

  public onClearAll(): void {
    this.clearAll.emit();
  }

  public getActiveCount(key: string): number {
    return this.activeFilters()[key]?.length ?? 0;
  }

  public onTitleClick(): void {
    if (this.suppressTitleClick) {
      this.suppressTitleClick = false;
      return;
    }
    if (this.showTitleDropdown() && this.titleEditable()) {
      this.titleSheetOpen.set(true);
    } else if (this.showTitleDropdown()) {
      this.titleDropdownClick.emit();
    } else if (this.titleEditable()) {
      this.openRename();
    }
  }

  public onTitlePointerDown(event: PointerEvent): void {
    this.suppressTitleClick = false;
    if (!this.showTitleDropdown() || !this.titleEditable()) {
      return;
    }
    this.clearTitleLongPress();
    this.titlePressOrigin = { x: event.clientX, y: event.clientY };
    this.titleLongPressTimer = setTimeout(() => {
      this.titleLongPressTimer = null;
      this.suppressTitleClick = true;
      this.openRename();
    }, TITLE_LONG_PRESS_MS);
  }

  public onTitlePointerMove(event: PointerEvent): void {
    const origin = this.titlePressOrigin;
    if (!origin) {
      return;
    }
    const distance = Math.hypot(
      event.clientX - origin.x,
      event.clientY - origin.y,
    );
    if (distance > TITLE_LONG_PRESS_SLOP_PX) {
      this.clearTitleLongPress();
    }
  }

  public clearTitleLongPress(): void {
    if (this.titleLongPressTimer) {
      clearTimeout(this.titleLongPressTimer);
      this.titleLongPressTimer = null;
    }
    this.titlePressOrigin = null;
  }

  public onTitleSheetNavigate(): void {
    this.titleSheetOpen.set(false);
    this.titleDropdownClick.emit();
  }

  public openRename(): void {
    this.titleSheetOpen.set(false);
    this.renameValue.set(this.title());
    this.renameOpen.set(true);
    afterNextRender(() => this.focusRenameInput(), {
      injector: this.injector,
    });
  }

  public closeRename(): void {
    this.renameInputRef()?.nativeElement.blur();
    this.renameOpen.set(false);
  }

  public onRenameInput(value: string): void {
    this.renameValue.set(value);
  }

  public clearRename(): void {
    this.renameValue.set('');
    this.renameInputRef()?.nativeElement.focus();
  }

  public submitRename(): void {
    if (!this.canSaveRename()) {
      return;
    }
    this.titleChange.emit(this.renameValue().trim());
    this.closeRename();
    this.highlightRenamedTitle();
  }

  private focusRenameInput(): void {
    const input = this.renameInputRef()?.nativeElement;
    if (!input) {
      return;
    }
    if (!input.isConnected) {
      if (this.renameOpen()) {
        requestAnimationFrame(() => this.focusRenameInput());
      }
      return;
    }
    input.focus({ preventScroll: true });
    input.select();
  }

  private highlightRenamedTitle(): void {
    if (this.titleRenamedTimer) {
      clearTimeout(this.titleRenamedTimer);
    }
    this.titleRenamed.set(false);
    requestAnimationFrame(() => {
      this.titleRenamed.set(true);
      this.titleRenamedTimer = setTimeout(() => {
        this.titleRenamedTimer = null;
        this.titleRenamed.set(false);
      }, TITLE_RENAMED_HIGHLIGHT_MS);
    });
  }
}
